import apiClient from './meta-client';
import type {
    LeadsDashboardData,
    LeadsDashboardParams,
    LeadsBaseAlcancavel,
    JanelaBase,
    LeadsControlePaginas,
    LeadsPagina,
    CanalLeads,
    OrdenarPaginas,
} from '@/types/leadsDashboard';

/**
 * Tela de Leads v7 — cliente da API (especificação §6.1).
 *
 * São dois endpoints de propósito: o consolidado carrega a tela, e o de
 * páginas recarrega SÓ o bloco de páginas quando o usuário mexe em
 * canal/ordenação/top/busca — que é o controle mais usado e o bloco mais caro.
 * Sem essa separação, trocar a ordenação recarregaria receita, séries e países
 * à toa.
 */

function toQuery(params: LeadsDashboardParams): Record<string, unknown> {
    const q: Record<string, unknown> = {};
    if (params.periodo) q.periodo = params.periodo;
    if (params.de) q.de = params.de;
    if (params.ate) q.ate = params.ate;
    if (params.pais) q.pais = params.pais;
    if (params.pagina_id) q.pagina_id = params.pagina_id;
    if (params.canal) q.canal = params.canal;
    if (params.janela_base) q.janela_base = params.janela_base;
    if (params.top) q.top = params.top;
    if (params.ordenar_por) q.ordenar_por = params.ordenar_por;
    if (params.busca) q.busca = params.busca;
    if (params.user_id) q.user_id = params.user_id;
    return q;
}

export const getLeadsDashboard = async (
    params: LeadsDashboardParams
): Promise<LeadsDashboardData> => {
    const { data } = await apiClient.get<{ success: boolean; data: LeadsDashboardData; error?: string }>(
        '/meta/leads/dashboard',
        { params: toQuery(params) }
    );
    if (!data.success) throw new Error(data.error || 'Falha ao carregar o dashboard de leads');
    return data.data;
};

export interface LeadsPaginasBloco {
    paginas: {
        ordenado_por: OrdenarPaginas;
        canal: CanalLeads;
        top: number;
        busca: string | null;
        fixada: LeadsPagina | null;
        _total_itens: number;
        itens: LeadsPagina[];
    };
    controle_paginas: LeadsControlePaginas;
}

/** Só o bloco da base, com janela própria até hoje — não recalcula a tela. */
export const getLeadsBaseAlcancavel = async (params: {
    janela_base: JanelaBase; pais?: string | null; pagina_id?: string | null; user_id?: number | null;
}): Promise<LeadsBaseAlcancavel> => {
    const { data } = await apiClient.get<{ success: boolean; data: LeadsBaseAlcancavel; error?: string }>(
        '/meta/leads/dashboard/base-alcancavel',
        { params: toQuery(params) }
    );
    if (!data.success) throw new Error(data.error || 'Falha ao carregar a base alcançável');
    return data.data;
};

export const getLeadsDashboardPaginas = async (
    params: LeadsDashboardParams
): Promise<LeadsPaginasBloco> => {
    const { data } = await apiClient.get<{ success: boolean; data: LeadsPaginasBloco; error?: string }>(
        '/meta/leads/dashboard/paginas',
        { params: toQuery(params) }
    );
    if (!data.success) throw new Error(data.error || 'Falha ao carregar as páginas');
    return data.data;
};
